/**
 * @file Which art each weapon archetype wears.
 *
 * Kept apart from {@link ./Weapons.js} on purpose: that table is balance, this
 * one is presentation. A weapon missing from here still works — the renderer
 * falls back to the procedural block it used before real models existed, and
 * the HUD simply shows no portrait.
 *
 * Source art is the Kenney "Weapon Pack" (CC0); the licence travels with the
 * copies in `assets/models/weapons/` and `assets/weapon_sprites/`.
 *
 * The `?url` imports are what let Vite fingerprint and rewrite these files for
 * the `/frenzy/` base path — never build these paths by hand.
 */

import flamethrowerLongModel from '../../assets/models/weapons/flamethrower_long.glb?url';
import knifeModel from '../../assets/models/weapons/knife_sharp.glb?url';
import flamethrowerShortModel from '../../assets/models/weapons/flamethrower_short.glb?url';
import machinegunLauncherModel from '../../assets/models/weapons/machinegunLauncher.glb?url';
import pistolModel from '../../assets/models/weapons/pistol.glb?url';
import pistolSilencerModel from '../../assets/models/weapons/pistolSilencer.glb?url';
import rocketlauncherModel from '../../assets/models/weapons/rocketlauncher.glb?url';
import shotgunModel from '../../assets/models/weapons/shotgun.glb?url';
import sniperModel from '../../assets/models/weapons/sniper.glb?url';
import uziModel from '../../assets/models/weapons/uzi.glb?url';

import flamethrowerLongSprite from '../../assets/weapon_sprites/flamethrower_long.png?url';
import knifeSprite from '../../assets/weapon_sprites/knife_sharp.png?url';
import flamethrowerShortSprite from '../../assets/weapon_sprites/flamethrower_short.png?url';
import machinegunLauncherSprite from '../../assets/weapon_sprites/machinegunLauncher.png?url';
import pistolSprite from '../../assets/weapon_sprites/pistol.png?url';
import rocketlauncherSprite from '../../assets/weapon_sprites/rocketlauncher.png?url';
import shotgunSprite from '../../assets/weapon_sprites/shotgun.png?url';
import sniperSprite from '../../assets/weapon_sprites/sniper.png?url';
import uziSprite from '../../assets/weapon_sprites/uzi.png?url';
import revolverSprite from '../../assets/weapon_sprites/revolver.png?url';
import gatlingSprite from '../../assets/weapon_sprites/gatling.png?url';

/**
 * Metres of weapon per unit of source model. The pack is internally consistent
 * (a pistol really is a third of a sniper), so one shared number keeps those
 * proportions instead of normalising every gun to the same length.
 */
export const WEAPON_MODEL_SCALE = 1.8;

/**
 * Emissive gain applied on top of the pack's flat greys. Low enough that the
 * moulded detail survives, high enough that the bloom pass still picks the
 * weapon out of a dark arena.
 */
export const WEAPON_MODEL_EMISSIVE = 0.34;

/**
 * @typedef {object} WeaponArt
 * @property {string} model URL of the `.glb` body.
 * @property {string} sprite URL of the HUD portrait.
 * @property {number} [scale] Overrides {@link WEAPON_MODEL_SCALE}.
 * @property {number} [yOffset] Lifts the model off its pivot, in model units,
 *   applied before `scale`. Used to sit a weapon on the floor rather than
 *   through it.
 */

/**
 * Weapon archetype key → art. Enemy-only archetypes are in here too: they end
 * up on the floor whenever their carrier is stunned or killed.
 * @type {Readonly<Record<string, WeaponArt>>}
 */
export const WEAPON_ART = Object.freeze({
  pistol: { model: pistolModel, sprite: pistolSprite },
  revolver: { model: pistolSilencerModel, sprite: revolverSprite },
  smg: { model: uziModel, sprite: uziSprite },
  gatling: { model: flamethrowerLongModel, sprite: gatlingSprite },
  shotgun: { model: shotgunModel, sprite: shotgunSprite },
  railgun: { model: sniperModel, sprite: sniperSprite },
  // The launcher is already the longest model in the pack; full scale makes it
  // read as wider than the player.
  rocket_launcher: { model: rocketlauncherModel, sprite: rocketlauncherSprite, scale: 1.5 },
  flamethrower: { model: flamethrowerLongModel, sprite: flamethrowerLongSprite },
  flamethrower_enemy: { model: flamethrowerShortModel, sprite: flamethrowerShortSprite },
  // No lobbing weapon in the pack; the drum-fed launcher is the closest read.
  spitter: { model: machinegunLauncherModel, sprite: machinegunLauncherSprite },
  // The fallback blade (issue #122). Scaled up from the shared factor: at pack
  // scale a knife is a speck in the hand next to a pistol.
  knife: { model: knifeModel, sprite: knifeSprite, scale: 2.6 }
});

/**
 * @returns {Array<{ key: string, url: string, type: 'model' }>} Manifest for
 *   {@link ../core/AssetLoader.js AssetLoader}, keyed by weapon archetype so a
 *   shared source file still yields one independently tinted copy per weapon.
 */
export function weaponModelManifest() {
  return Object.entries(WEAPON_ART).map(([key, art]) => ({
    key: `weapon-model:${key}`,
    url: art.model,
    type: /** @type {'model'} */ ('model')
  }));
}

/**
 * @param {string} key Weapon archetype key.
 * @returns {string} Asset-loader key for that weapon's model.
 */
export function weaponModelKey(key) {
  return `weapon-model:${key}`;
}

/**
 * @param {string | null | undefined} key Weapon archetype key.
 * @returns {string | null} HUD portrait URL, or null for a weapon with no art.
 */
export function weaponSprite(key) {
  return (key && WEAPON_ART[key]?.sprite) ?? null;
}
